/**
 * Iter53 P33 — SupplierFieldsEditDialog extras din SaleDetail.js.
 *
 * Dialog pentru editare manuală câmpuri furnizor (supplier_status / supplier_status_date / csd_date)
 * + motiv audit. Folosește endpoint PUT /api/contracts/{id}/supplier-fields.
 *
 * Trigger hydrate când dialog deschide → setForm cu valorile curente din contract.
 */
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierFieldsEditDialog({
  open, onOpenChange,
  sale,
  form, setForm,
  onSaved,
}) {
  const handleOpenChange = (o) => {
    if (o) {
      // hydrate form from current contract values when opening
      setForm({
        supplier_status: sale?.contract?.supplier_status || "",
        supplier_status_date: sale?.contract?.supplier_status_date
          ? new Date(sale.contract.supplier_status_date).toISOString().slice(0, 16)
          : "",
        csd_date: sale?.contract?.csd_date
          ? sale.contract.csd_date.split(".").reverse().join("-")
          : "",
        reason: "",
      });
    }
    onOpenChange(o);
  };

  const save = async () => {
    if (!sale?.contract?.id) return;
    try {
      const payload = {
        supplier_status: form.supplier_status || null,
        supplier_status_date: form.supplier_status_date || null,
        csd_date: form.csd_date || null,
        reason: form.reason || null,
        fields_to_patch: ["supplier_status", "supplier_status_date", "csd_date"],
      };
      const res = await axios.put(
        `${API_URL}/contracts/${sale.contract.id}/supplier-fields`,
        payload,
      );
      toast.success(`Salvat: ${res.data.changes?.length || 0} câmpuri modificate`);
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" data-testid="supplier-edit-dialog">
        <DialogHeader>
          <DialogTitle>Editează câmpuri furnizor (PPC, fost ENEL)</DialogTitle>
          <DialogDescription>
            Modificările sunt înregistrate cu source=manual în field_sources și audit log.
            SF import poate suprascrie ulterior cu un banner de avertizare 24h.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Status furnizor</Label>
            <select
              className="w-full border rounded-md p-2 text-sm bg-white"
              value={form.supplier_status || ""}
              onChange={(e) => setForm({ ...form, supplier_status: e.target.value })}
              data-testid="supplier-form-status"
            >
              <option value="">— fără status —</option>
              <option value="Signed">Signed (semnat)</option>
              <option value="Activated">Activated (activ)</option>
              <option value="Terminated">Terminated (reziliat)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Data status furnizor</Label>
            <Input
              type="datetime-local"
              value={form.supplier_status_date || ""}
              onChange={(e) => setForm({ ...form, supplier_status_date: e.target.value })}
              data-testid="supplier-form-date"
            />
          </div>
          <div>
            <Label className="text-xs">Data CSD (începere furnizare)</Label>
            <Input
              type="date"
              value={form.csd_date || ""}
              onChange={(e) => setForm({ ...form, csd_date: e.target.value })}
              data-testid="supplier-form-csd"
            />
          </div>
          <div>
            <Label className="text-xs">Motiv modificare (opțional, intră în audit)</Label>
            <Input
              value={form.reason || ""}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              data-testid="supplier-form-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="supplier-cancel">
            Anulează
          </Button>
          <Button
            onClick={save}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="supplier-save"
          >
            Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
